import React from "react";
import ReactDatePicker, { CalendarContainer } from "react-datepicker";
import {
  Typography,
  Stack,
  Box,
  IconButton,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent
} from "@mui/material";
import dayjs from "dayjs";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import RightArrowIcon from "@mui/icons-material/EastRounded";
import { setInSessionStorage, toDate } from "utils/common";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import "./DatePicker.scss";
import * as constants from "helpers/constants";
import DateOptions from "types/DateOptions.enum";
import DatePickerProps from "./DatePicker.types";

const DateContext = React.createContext({
  from: null,
  to: null,
  closeDatePicker: () => null
});

const DatePickerContainer = ({ className, children }) => {
  const { from, to, closeDatePicker } = React.useContext(DateContext);

  return (
    <CalendarContainer className={className}>
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={2} mx={1} mt={1}>
          <CalendarMonthIcon color="gray" />
          <Typography variant="caption" alignSelf="center">
            {_.defaultTo(toDate(from, constants.DATE_FORMAT), "Start")}
          </Typography>
          <RightArrowIcon color="gray" />
          <Typography variant="caption" alignSelf="center">
            {_.defaultTo(toDate(to, constants.DATE_FORMAT), "End")}
          </Typography>
        </Stack>
        <IconButton onClick={closeDatePicker}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Box position="relative">{children}</Box>
    </CalendarContainer>
  );
};

function DatePicker(props: DatePickerProps) {
  const { t } = useTranslation();
  const [{ from, to }, setDatePickerState] = React.useState({
    from: null,
    to: null
  });
  const datePickerRef = React.useRef(null);

  function openDatePicker() {
    if (getDateRangeType() === DateOptions.Custom) {
      setDatePickerState({
        from: dayjs(props.tableState.startDate).toDate(),
        to: dayjs(props.tableState.endDate).toDate()
      });
    } else {
      resetCustomDate();
    }
    datePickerRef.current.setOpen(true);
  }

  function applyCustomDate() {
    if (from) {
      const endDate = dayjs(to).isValid() ? dayjs(to) : dayjs(from);

      if (props.persistKey) {
        setInSessionStorage(props.persistKey, {
          startDate: dayjs(from).startOf("day").toISOString(),
          endDate: endDate.endOf("day").toISOString()
        });
      }

      props.setTableState((prev) => ({
        ...prev,
        startDate: dayjs(from).startOf("day").toISOString(),
        endDate: endDate.endOf("day").toISOString(),
        pageNumber: 1
      }));
    }
    closeDatePicker();
  }

  function resetCustomDate() {
    setDatePickerState({ from: null, to: null });
  }

  function closeDatePicker() {
    resetCustomDate();
    datePickerRef.current.setOpen(false);
  }

  function getDateRangeType() {
    if (
      dayjs()
        .startOf("day")
        .isSame(dayjs(props.tableState.startDate).startOf("day")) &&
      dayjs().endOf("day").isSame(dayjs(props.tableState.endDate).endOf("day"))
    ) {
      return DateOptions.Today;
    } else if (
      dayjs()
        .subtract(1, "day")
        .startOf("day")
        .isSame(dayjs(props.tableState.startDate).startOf("day")) &&
      dayjs()
        .subtract(1, "day")
        .endOf("day")
        .isSame(dayjs(props.tableState.endDate).endOf("day"))
    ) {
      return DateOptions.Yesterday;
    } else if (
      dayjs()
        .subtract(6, "day")
        .startOf("day")
        .isSame(dayjs(props.tableState.startDate).startOf("day")) &&
      dayjs().endOf("day").isSame(dayjs(props.tableState.endDate).endOf("day"))
    ) {
      return DateOptions.LastSevenDays;
    } else if (
      dayjs()
        .subtract(29, "day")
        .startOf("day")
        .isSame(dayjs(props.tableState.startDate).startOf("day")) &&
      dayjs().endOf("day").isSame(dayjs(props.tableState.endDate).endOf("day"))
    ) {
      return DateOptions.LastThirtyDays;
    } else if (
      dayjs()
        .subtract(6, "months")
        .startOf("day")
        .isSame(dayjs(props.tableState.startDate).startOf("day")) &&
      dayjs().endOf("day").isSame(dayjs(props.tableState.endDate).endOf("day"))
    ) {
      return DateOptions.LastSixMonths;
    } else if (
      dayjs()
        .subtract(1, "year")
        .startOf("day")
        .isSame(dayjs(props.tableState.startDate).startOf("day")) &&
      dayjs().endOf("day").isSame(dayjs(props.tableState.endDate).endOf("day"))
    ) {
      return DateOptions.LastOneYear;
    } else {
      return DateOptions.Custom;
    }
  }

  function renderDateValue() {
    const dateRangeType = getDateRangeType();

    switch (dateRangeType) {
      case DateOptions.Today:
        return t("datePicker.today");
      case DateOptions.Yesterday:
        return t("datePicker.yesterday");
      case DateOptions.LastSevenDays:
        return t("datePicker.lastSevenDays");
      case DateOptions.LastThirtyDays:
        return t("datePicker.lastThirtyDays");
      case DateOptions.LastSixMonths:
        return t("datePicker.lastSixMonths");
      case DateOptions.LastOneYear:
        return t("datePicker.lastOneYear");
      default:
        return (
          <Stack direction="row" spacing={2} alignContent="center">
            <Typography
              variant="subtitle2"
              fontWeight="regular"
              alignSelf="center"
            >
              {_.defaultTo(
                toDate(props.tableState.startDate, constants.DATE_FORMAT),
                t("datePicker.start")
              )}
            </Typography>
            <RightArrowIcon color="gray" />
            <Typography
              variant="subtitle2"
              fontWeight="regular"
              alignSelf="center"
            >
              {_.defaultTo(
                toDate(props.tableState.endDate, constants.DATE_FORMAT),
                t("datePicker.end")
              )}
            </Typography>
          </Stack>
        );
    }
  }

  function handleDateChange(event: SelectChangeEvent) {
    if (event.target.value !== DateOptions.Custom) {
      let startDate = null;
      let endDate = dayjs().endOf("day");

      switch (event.target.value) {
        case DateOptions.Today:
          startDate = dayjs().startOf("day");
          break;
        case DateOptions.Yesterday:
          startDate = dayjs().subtract(1, "day").startOf("day");
          endDate = dayjs().subtract(1, "day").endOf("day");
          break;
        case DateOptions.LastSevenDays:
          startDate = dayjs().subtract(6, "days").startOf("day");
          break;
        case DateOptions.LastThirtyDays:
          startDate = dayjs().subtract(29, "days").startOf("day");
          break;
        case DateOptions.LastSixMonths:
          startDate = dayjs().subtract(6, "months").startOf("day");
          break;
        case DateOptions.LastOneYear:
          startDate = dayjs().subtract(1, "year").startOf("day");
          break;
      }

      if (props.persistKey) {
        setInSessionStorage(props.persistKey, {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });
      }

      props.setTableState((prev) => ({
        ...prev,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        pageNumber: 1
      }));
    }
  }

  const handleCustomDateChange = (dates) => {
    const [from, to] = dates;
    setDatePickerState({ from, to });
  };

  const getMaxDate = () => {
    if (from) {
      const maxDate = dayjs(from)
        .add(props.maxDurationInMonths, "months")
        .subtract(1, "day")
        .toDate();

      return dayjs().isSameOrBefore(maxDate) ? dayjs().toDate() : maxDate;
    } else {
      return dayjs().toDate();
    }
  };

  return (
    <>
      <DateContext.Provider value={{ from, to, closeDatePicker }}>
        <ReactDatePicker
          calendarContainer={DatePickerContainer}
          ref={datePickerRef}
          popperClassName="date-picker"
          selected={null}
          onChange={handleCustomDateChange}
          openToDate={from}
          startDate={from}
          endDate={to}
          minDate={from}
          maxDate={getMaxDate()}
          onClickOutside={resetCustomDate}
          selectsRange
          monthsShown={2}
          disabledKeyboardNavigation
          shouldCloseOnSelect={false}
          popperPlacement="bottom"
          customInput={<input style={{ width: 0, visibility: "hidden" }} />}
          popperModifiers={[
            {
              name: "offset",
              options: {
                offset: [80]
              }
            }
          ]}
        >
          <Button variant="text" onClick={resetCustomDate}>
            {t("common.reset")}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={applyCustomDate}
            disabled={!from && !to}
          >
            {t("common.apply")}
          </Button>
        </ReactDatePicker>
      </DateContext.Provider>
      <Select
        displayEmpty
        value={getDateRangeType()}
        onChange={handleDateChange}
        renderValue={renderDateValue}
        inputProps={{
          sx: { minWidth: 110, textAlign: "center" }
        }}
        sx={{
          height: 40,
          marginRight: 1
        }}
      >
        <MenuItem
          value={DateOptions.Today}
          classes={{
            root: "date-picker__date-filter-dropdown__date-item"
          }}
        >
          {t("datePicker.today")}
        </MenuItem>
        <MenuItem
          value={DateOptions.Yesterday}
          classes={{
            root: "date-picker__date-filter-dropdown__date-item"
          }}
        >
          {t("datePicker.yesterday")}
        </MenuItem>
        <MenuItem
          value={DateOptions.LastSevenDays}
          classes={{
            root: "date-picker__date-filter-dropdown__date-item"
          }}
        >
          {t("datePicker.lastSevenDays")}
        </MenuItem>
        <MenuItem
          value={DateOptions.LastThirtyDays}
          classes={{
            root: "date-picker__date-filter-dropdown__date-item"
          }}
        >
          {t("datePicker.lastThirtyDays")}
        </MenuItem>
        {props.showLast6Months && (
          <MenuItem
            value={DateOptions.LastSixMonths}
            classes={{
              root: "date-picker__date-filter-dropdown__date-item"
            }}
          >
            {t("datePicker.lastSixMonths")}
          </MenuItem>
        )}
        {props.showLast1Year && (
          <MenuItem
            value={DateOptions.LastOneYear}
            classes={{
              root: "date-picker__date-filter-dropdown__date-item"
            }}
          >
            {t("datePicker.lastOneYear")}
          </MenuItem>
        )}
        <MenuItem
          value={DateOptions.Custom}
          onClick={openDatePicker}
          classes={{
            root: "date-picker__date-filter-dropdown__date-item"
          }}
        >
          {t("datePicker.custom")}
        </MenuItem>
      </Select>
    </>
  );
}

export default DatePicker;
